import { selectColorFinderFlightById } from "features/entity-caching/flights/selectors"
import { useMemo } from "react"
import { useAppSelector } from "integrations/redux/hooks"
import {
    selectColorFinderFlightId,
    selectColorFinderIsOpen,
    selectColorFinderMapSettings,
} from "../../V2/redux/selectors"
import { CompositeLayer, CompositeLayerProps, Layer } from "@deck.gl/core"
import { useColorFinderMetadata } from "../../V2/ColorFinderQueries"
import { convertHexStringToRGBArray } from "@nova/util-strings"
import { LngLatCoordinatePair } from "features/map/event/types"
import { VideoMetadata } from "@nova/slice-video"
import { subscribeToFootageEvent } from "plugins/ColorFinder/V2/ColorFinderEvents"
import { getVideoFrameBboxAsync } from "features/map/layers/video/VideoProjectionLayer"
import { IconLayer, PolygonLayer } from "@deck.gl/layers"
import FlightPathsLayer from "features/map/layers/flights/FlightPathsLayer.class"
import { getGlobalTerrainData } from "features/map/GlobalTerrainManager"

export type FootageFrameLayerProps = CompositeLayerProps & {
    metadata: VideoMetadata | null
    lineColor: [number, number, number]

    crosshairEnabled: boolean
    fieldOfViewEnabled: boolean
    flightPathEnabled: boolean
}

export type ColorFinderLayerState = {
    bbox:
        | [LngLatCoordinatePair, LngLatCoordinatePair, LngLatCoordinatePair, LngLatCoordinatePair]
        | undefined
    centerPoint: LngLatCoordinatePair | undefined
    unsubscribe: () => void
    frame: VideoMetadata[number] | undefined
}

export class FootageFrameLayer
    extends CompositeLayer<FootageFrameLayerProps>
    implements Layer<FootageFrameLayerProps>
{
    static defaultProps = {
        crosshairEnabled: true,
        fieldOfViewEnabled: true,
        flightPathEnabled: true,
    }

    initializeState() {
        this.setState({
            bbox: undefined,
            centerPoint: undefined,
            unsubscribe: () => {},
        })
        this.subscribe()
    }

    updateState({
        props,
        oldProps,
    }: {
        props: FootageFrameLayerProps
        oldProps: FootageFrameLayerProps
    }) {
        if (props.metadata !== oldProps.metadata) {
            ;(this.state as ColorFinderLayerState).unsubscribe()
            this.subscribe()
        }
    }

    finalizeState() {
        ;(this.state as ColorFinderLayerState).unsubscribe()
    }

    calculatePolygonCenter(polygon: [number, number][]): [number, number] {
        if (!polygon) return [0, 0]
        let sumX = 0,
            sumY = 0
        for (const p of polygon) {
            sumX += p[0]
            sumY += p[1]
        }
        const count = polygon.length
        return [sumX / count, sumY / count] as const
    }

    subscribe() {
        const { metadata } = this.props
        if (!metadata) return

        const unsubscribe = subscribeToFootageEvent(async (event) => {
            if (!this.props.metadata) return

            const frame = this.props.metadata[event.frame]
            if (!frame) return

            const newBbox = await getVideoFrameBboxAsync(frame)
            if (!newBbox) return

            const newCenterPoint = this.calculatePolygonCenter(newBbox)

            this.setState({
                bbox: newBbox,
                centerPoint: newCenterPoint,
                frame,
            })
        })

        this.setState({ unsubscribe })
    }

    renderLayers() {
        const { id, crosshairEnabled, fieldOfViewEnabled, flightPathEnabled, metadata } = this.props
        const { bbox, centerPoint, frame } = this.state as ColorFinderLayerState
        const layers: Layer[] = []

        if (flightPathEnabled) {
            const frames = metadata || []
            const validPathPoints = frames.filter(
                (point) => Math.abs(point[0]) > 1 && Math.abs(point[1]) > 1
            )

            layers.push(
                new FlightPathsLayer({
                    id: `${id}-flight-path`,
                    data: validPathPoints.length
                        ? [
                              {
                                  path: validPathPoints,
                              },
                          ]
                        : [],
                    extensions: [...getGlobalTerrainData().extensions],
                    terrainDrawMode: "offset",
                    visible: (frames.length || 0) > 0,
                    getColor: () => [152, 227, 220],
                })
            )
        }

        if (bbox && fieldOfViewEnabled) {
            layers.push(
                new PolygonLayer({
                    id: `${id}-fov`,
                    data: [bbox],
                    getPolygon: (d) => d,

                    filled: false,
                    stroked: true,
                    getLineColor: convertHexStringToRGBArray("#0971CE"),
                    getLineWidth: 2,
                    lineWidthUnits: "pixels",
                })
            )
        }

        if (crosshairEnabled && centerPoint && frame) {
            layers.push(
                new IconLayer({
                    id: `${id}-center-icon`,
                    pickable: false,
                    data: [centerPoint],
                    getPosition: (d) => d,
                    getIcon: () => ({
                        url: `/icons/shape/svg/Plus.svg`,
                        width: 512,
                        height: 512,
                        mask: true,
                    }),
                    sizeUnits: "pixels",
                    sizeScale: 20,
                    getColor: () => convertHexStringToRGBArray("#0971CE"),
                    getAngle: () => -frame[4],
                    billboard: false,
                    getSize: 1,
                })
            )
        }

        return layers
    }
}

export const useCFFrameBBOXLayer = () => {
    const isOpen = useAppSelector(selectColorFinderIsOpen)
    const flightId = useAppSelector(selectColorFinderFlightId)
    const flight = useAppSelector(selectColorFinderFlightById(flightId))

    const { crosshair, fieldOfView, flightPath } = useAppSelector(selectColorFinderMapSettings)
    const { data: metadata } = useColorFinderMetadata(flight)

    const layer = useMemo(() => {
        if (!flightId || !isOpen || !metadata) {
            return null
        }

        const l = new FootageFrameLayer({
            id: `${flightId}-fov`,
            metadata: metadata,
            crosshairEnabled: crosshair,
            fieldOfViewEnabled: fieldOfView,
            flightPathEnabled: flightPath,
        })

        return l
    }, [crosshair, fieldOfView, flightId, flightPath, isOpen, metadata])

    return (layer ? [layer] : []) as unknown as Layer[]
}
